// LocalStorage utility for managing app settings

// Storage keys
export const STORAGE_KEYS = {
  PAGINATION_VALUE: "paginationValue",
  USER_PREFERENCES: "userPreferences",
  THEME: "theme",
  LANGUAGE: "language",
  FOLDER_MAPPINGS: "folderMappings", // New key for fname/fsource pairs
  INTEGRATION_PAGINATION_VALUE: "integrationPaginationValue",
  INTEGRATION_FILTERS: "integrationFilters",
} as const;

// Default values
export const DEFAULT_VALUES = {
  PAGINATION_VALUE: 25,
  THEME: "light",
  LANGUAGE: "en",
  INTEGRATION_PAGINATION_VALUE: 25,
  INTEGRATION_FILTERS: {
    nextRunFilter: "",
    lastRunFilter: "",
    search: "",
  },
} as const;

// Type definitions
export interface UserPreferences {
  paginationValue: number;
  theme: "light" | "dark";
  language: string;
  integrationPaginationValue: number;
  integrationFilters: {
    nextRunFilter: string;
    lastRunFilter: string;
    search: string;
  };
}

// Generic localStorage methods
export const localStorageUtils = {
  // Set item with error handling
  setItem: <T>(key: string, value: T): boolean => {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      return false;
    }
  },

  // Get item with error handling and type safety
  getItem: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      return defaultValue;
    }
  },

  // Remove item
  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  },

  // Clear all app data
  clearAppData: (): boolean => {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      return false;
    }
  },

  // Check if localStorage is available
  isAvailable: (): boolean => {
    try {
      const test = "__localStorage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  },
};

// Pagination-specific methods
export const paginationStorage = {
  // Get pagination value from localStorage
  getPaginationValue: (): number => {
    return localStorageUtils.getItem<number>(
      STORAGE_KEYS.PAGINATION_VALUE,
      DEFAULT_VALUES.PAGINATION_VALUE,
    );
  },

  // Set pagination value to localStorage
  setPaginationValue: (value: number): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.PAGINATION_VALUE, value);
  },

  // Validate and set pagination value
  setValidPaginationValue: (value: number, validValues: number[]): boolean => {
    if (validValues.includes(value)) {
      return paginationStorage.setPaginationValue(value);
    }
    return false;
  },

  // Clear pagination data
  clearPaginationData: (): boolean => {
    return localStorageUtils.removeItem(STORAGE_KEYS.PAGINATION_VALUE);
  },

  // Reset to default value
  resetToDefault: (): boolean => {
    return paginationStorage.setPaginationValue(
      DEFAULT_VALUES.PAGINATION_VALUE,
    );
  },
};

// User preferences methods
export const userPreferencesStorage = {
  // Get all user preferences
  getUserPreferences: (): UserPreferences => {
    return localStorageUtils.getItem<UserPreferences>(
      STORAGE_KEYS.USER_PREFERENCES,
      {
        paginationValue: DEFAULT_VALUES.PAGINATION_VALUE,
        theme: DEFAULT_VALUES.THEME as "light" | "dark",
        language: DEFAULT_VALUES.LANGUAGE,
        integrationPaginationValue: DEFAULT_VALUES.INTEGRATION_PAGINATION_VALUE,
        integrationFilters: DEFAULT_VALUES.INTEGRATION_FILTERS,
      },
    );
  },

  // Set user preferences
  setUserPreferences: (preferences: Partial<UserPreferences>): boolean => {
    const current = userPreferencesStorage.getUserPreferences();
    const updated = { ...current, ...preferences };
    return localStorageUtils.setItem(STORAGE_KEYS.USER_PREFERENCES, updated);
  },

  // Update specific preference
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ): boolean => {
    const current = userPreferencesStorage.getUserPreferences();
    return userPreferencesStorage.setUserPreferences({ [key]: value });
  },
};

// Theme methods
export const themeStorage = {
  getTheme: (): "light" | "dark" => {
    return localStorageUtils.getItem<"light" | "dark">(
      STORAGE_KEYS.THEME,
      DEFAULT_VALUES.THEME as "light" | "dark",
    );
  },

  setTheme: (theme: "light" | "dark"): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.THEME, theme);
  },
};

// Language methods
export const languageStorage = {
  getLanguage: (): string => {
    return localStorageUtils.getItem<string>(
      STORAGE_KEYS.LANGUAGE,
      DEFAULT_VALUES.LANGUAGE,
    );
  },

  setLanguage: (language: string): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.LANGUAGE, language);
  },
};

// Folder mappings methods for fname/fsource pairs
/**
 * Storage utility for managing folder name mappings.
 *
 * This utility stores pairs of folder names (fname) and their corresponding
 * source paths (fsource) in localStorage. This allows the application to:
 * - Display user-friendly folder names in the UI
 * - Use the actual source paths for API requests and navigation
 *
 * Example usage:
 * - Store: folderMappingsStorage.setFolderMapping("My Documents", "john/documents")
 * - Display: folderMappingsStorage.getFname("john/documents") returns "My Documents"
 * - API: Use "john/documents" for API requests
 */
export const folderMappingsStorage = {
  // Get all folder mappings
  getFolderMappings: (): Record<string, string> => {
    return localStorageUtils.getItem<Record<string, string>>(
      STORAGE_KEYS.FOLDER_MAPPINGS,
      {},
    );
  },

  // Set folder mappings
  setFolderMappings: (mappings: Record<string, string>): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.FOLDER_MAPPINGS, mappings);
  },

  // Add or update a single folder mapping
  setFolderMapping: (fname: string, fsource: string): boolean => {
    const currentMappings = folderMappingsStorage.getFolderMappings();
    const updatedMappings = { ...currentMappings, [fname]: fsource };
    return folderMappingsStorage.setFolderMappings(updatedMappings);
  },

  // Get fsource for a given fname
  getFsource: (fname: string): string | null => {
    const mappings = folderMappingsStorage.getFolderMappings();
    return mappings[fname] || null;
  },

  // Get fname for a given fsource
  getFname: (fsource: string): string | null => {
    const mappings = folderMappingsStorage.getFolderMappings();
    for (const [fname, storedFsource] of Object.entries(mappings)) {
      if (storedFsource === fsource) {
        return fname;
      }
    }
    return null;
  },

  // Remove a folder mapping
  removeFolderMapping: (fname: string): boolean => {
    const currentMappings = folderMappingsStorage.getFolderMappings();
    const { [fname]: removed, ...updatedMappings } = currentMappings;
    return folderMappingsStorage.setFolderMappings(updatedMappings);
  },

  // Clear all folder mappings
  clearFolderMappings: (): boolean => {
    return localStorageUtils.removeItem(STORAGE_KEYS.FOLDER_MAPPINGS);
  },

  // Clear folder mappings that are no longer relevant to the current context
  clearIrrelevantMappings: (currentPrefix: string): boolean => {
    const mappings = folderMappingsStorage.getFolderMappings();
    const currentPathParts = currentPrefix.split("/").filter((part) => part);

    // Keep only mappings that are still relevant to the current path
    const relevantMappings: Record<string, string> = {};

    for (const [fname, fsource] of Object.entries(mappings)) {
      const mappingPathParts = fsource.split("/").filter((part) => part);

      // Check if this mapping is still relevant to the current context
      let isRelevant = false;

      // If we're at root level, keep all mappings
      if (currentPathParts.length === 0) {
        isRelevant = true;
      } else {
        // Check if the mapping's path starts with the current prefix
        // or if the current prefix starts with the mapping's path
        const mappingPrefix = mappingPathParts
          .slice(0, currentPathParts.length)
          .join("/");
        const currentPrefixStr = currentPathParts.join("/");

        // A mapping is relevant if:
        // 1. It's exactly at the current level
        // 2. It's a child of the current level (deeper)
        // 3. It's a parent of the current level (higher)
        isRelevant =
          mappingPrefix === currentPrefixStr ||
          currentPrefixStr.startsWith(mappingPrefix + "/") ||
          mappingPrefix.startsWith(currentPrefixStr + "/");
      }

      if (isRelevant) {
        relevantMappings[fname] = fsource;
      }
    }

    return folderMappingsStorage.setFolderMappings(relevantMappings);
  },

  // Clear folder mappings when navigating to a higher level (going up in hierarchy)
  clearMappingsForHigherLevel: (newPrefix: string): boolean => {
    const mappings = folderMappingsStorage.getFolderMappings();
    const newPathParts = newPrefix.split("/").filter((part) => part);

    // Keep only mappings that are at or above the new level
    const relevantMappings: Record<string, string> = {};

    for (const [fname, fsource] of Object.entries(mappings)) {
      const mappingPathParts = fsource.split("/").filter((part) => part);

      // A mapping is relevant if it's at or above the new level
      // (i.e., it has the same or fewer path parts than the new prefix)
      const isRelevant = mappingPathParts.length <= newPathParts.length;

      if (isRelevant) {
        relevantMappings[fname] = fsource;
      }
    }

    return folderMappingsStorage.setFolderMappings(relevantMappings);
  },

  // Check if a folder mapping exists
  hasFolderMapping: (fname: string): boolean => {
    const mappings = folderMappingsStorage.getFolderMappings();
    return fname in mappings;
  },

  // Test function to verify clearing logic
  testClearingLogic: () => {
    // Test case 1: Going up in hierarchy
    folderMappingsStorage.setFolderMappings({
      paystubs: "netchex/data/paystubs",
      Netchex: "netchex/data",
      documents: "netchex/data/documents",
    });

    folderMappingsStorage.clearMappingsForHigherLevel("netchex/data");

    // Test case 2: Going down in hierarchy
    folderMappingsStorage.setFolderMappings({
      Netchex: "netchex/data",
      documents: "netchex/data/documents",
    });

    folderMappingsStorage.clearIrrelevantMappings("netchex/data/documents");
  },
};

// Export default for convenience
export default {
  localStorageUtils,
  paginationStorage,
  userPreferencesStorage,
  themeStorage,
  languageStorage,
  folderMappingsStorage,
  STORAGE_KEYS,
  DEFAULT_VALUES,
};
